/**
 * RoomEditor Component
 *
 * Modal form for adding/editing a room with reference images.
 * Follows the same pattern as ObjectEditor.jsx
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  X,
  Upload,
  Image as ImageIcon,
  Trash2,
  AlertCircle,
  Camera,
  CheckCircle
} from 'lucide-react';

const RoomEditor = ({
  room,
  stops = [],
  guideId,
  guideName,
  onSave,
  onClose,
  isSaving
}) => {
  const isEditing = !!room;
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    roomName: room?.roomName || '',
    stopId: room?.stopId || '',
    floor: room?.metadata?.floor || '',
    description: room?.metadata?.description || ''
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const handleFileSelect = useCallback((files) => {
    const validFiles = Array.from(files).filter(file => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return false;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      setErrors(prev => ({ ...prev, images: 'Please select valid image files (max 10MB each)' }));
      return;
    }

    // Check total count
    const totalCount = selectedFiles.length + validFiles.length;
    if (totalCount > 20) {
      setErrors(prev => ({ ...prev, images: 'Maximum 20 images allowed' }));
      return;
    }

    // Generate previews
    const newPreviews = validFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);

    // Clear error
    if (errors.images) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.images;
        return next;
      });
    }
  }, [selectedFiles, errors]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleRemoveImage = useCallback((index) => {
    // Revoke object URL to free memory
    URL.revokeObjectURL(previews[index].url);

    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, [previews]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.roomName.trim()) {
      newErrors.roomName = 'Room name is required';
    }

    if (!isEditing && selectedFiles.length === 0) {
      newErrors.images = 'At least one reference image is required';
    }

    if (!isEditing && selectedFiles.length < 3) {
      newErrors.images = 'At least 3 reference images recommended for accurate recognition';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ||
           (Object.keys(newErrors).length === 1 && newErrors.images?.includes('recommended'));
  }, [formData, selectedFiles, isEditing]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const roomData = {
      roomName: formData.roomName.trim(),
      stopId: formData.stopId || null,
      audioGuideId: guideId
    };

    const metadata = {};
    if (formData.floor) metadata.floor = formData.floor;
    if (formData.description) metadata.description = formData.description;

    await onSave(roomData, selectedFiles, metadata);
  }, [formData, selectedFiles, guideId, validateForm, onSave]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col my-auto" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Camera className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Add More Images' : 'Add New Room'}
              </h2>
              <p className="text-sm text-gray-500">{guideName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          <div className="space-y-6">
            {/* Room Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Room Name *
              </label>
              <input
                type="text"
                value={formData.roomName}
                onChange={(e) => handleChange('roomName', e.target.value)}
                placeholder="e.g., Mona Lisa Room, Egyptian Gallery"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.roomName ? 'border-red-300' : 'border-gray-200'
                }`}
                disabled={isEditing}
              />
              {errors.roomName && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.roomName}
                </p>
              )}
            </div>

            {/* Associate with Stop */}
            {!isEditing && stops.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link to Stop (Optional)
                </label>
                <select
                  value={formData.stopId}
                  onChange={(e) => handleChange('stopId', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">-- No linked stop --</option>
                  {stops.map((stop) => (
                    <option key={stop.id || stop.stopId} value={stop.id || stop.stopId}>
                      {stop.name || stop.title || `Stop ${stop.order || stop.id}`}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  When this room is recognized, the linked stop's audio will play
                </p>
              </div>
            )}

            {/* Floor */}
            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Floor (Optional)
                </label>
                <input
                  type="text"
                  value={formData.floor}
                  onChange={(e) => handleChange('floor', e.target.value)}
                  placeholder="e.g., Ground Floor, Level 2"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Reference Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reference Images *
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Upload 3-10 photos of the room from different angles. More variety = better recognition.
              </p>

              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-purple-500 bg-purple-50'
                    : errors.images
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />

                <Upload className={`w-10 h-10 mx-auto mb-3 ${
                  dragActive ? 'text-purple-500' : 'text-gray-400'
                }`} />

                <p className="text-sm text-gray-600 mb-2">
                  Drag & drop images here, or{' '}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-purple-600 font-medium hover:underline"
                  >
                    browse files
                  </button>
                </p>
                <p className="text-xs text-gray-400">
                  JPEG, PNG up to 10MB each • Max 20 images
                </p>
              </div>

              {errors.images && (
                <p className={`mt-2 text-sm flex items-center gap-1 ${
                  errors.images.includes('recommended') ? 'text-yellow-600' : 'text-red-500'
                }`}>
                  <AlertCircle className="w-4 h-4" />
                  {errors.images}
                </p>
              )}

              {/* Image Previews */}
              {previews.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      Selected Images ({previews.length})
                    </p>
                    {previews.length >= 3 && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Good coverage
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {previews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="mt-4 bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Tips for best results:</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Take photos from different angles and positions</li>
                  <li>• Include distinctive features (artwork, architecture, signs)</li>
                  <li>• Capture different lighting conditions if possible</li>
                  <li>• Avoid photos with too many people blocking the view</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || (selectedFiles.length === 0)}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
              isSaving || selectedFiles.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <ImageIcon className="w-4 h-4" />
                {isEditing ? 'Add Images' : 'Create Room'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomEditor;
